import jwt from 'jsonwebtoken';

import { LocalStorageHelper } from '@/helpers/local-storage.helper';
import apiClient from '@/services/api-client';

/**
 * Opens Gamayun (ETS filling assistant) and hands it the current ETS session.
 *
 * Protocol (ADR 0002 of Gamayun): ETS UI opens `<Gamayun>/launch` in the `gamayun` tab and waits
 * for `{ type: 'gamayun:ready' }` from that tab and the Gamayun origin, then answers
 * `{ type: 'gamayun:session', access_token, refresh_token, employee }` to the Gamayun origin only.
 * Tokens are never logged and never put into the URL.
 */

/**
 * @typedef {import('@/models/user.interface').User} User
 *
 * @typedef {Object} GamayunLaunchOptions
 * @property {string} launchUrl Gamayun launch page
 * @property {string} gamayunOrigin the only origin the session is sent to
 * @property {() => User | null} getEmployee current ETS user
 * @property {() => void} [onError] called if the session could not be handed over
 *
 * @typedef {Object} PendingLaunch
 * @property {(event: MessageEvent) => void} listener
 * @property {ReturnType<typeof setTimeout>} timer
 */

export const GAMAYUN_WINDOW_NAME = 'gamayun';
/** How long ETS UI waits for `gamayun:ready` after the click, ms */
export const GAMAYUN_READY_TIMEOUT_MS = 60 * 1000;
/** An access token with less time left is refreshed before the handoff, s */
const MIN_ACCESS_TOKEN_TTL = 60;

/** @type {PendingLaunch | null} */
let pendingLaunch = null;

/**
 * @param {string | null} token
 * @returns {number} seconds until the token expires, 0 for a missing or unreadable token
 */
const getSecondsLeft = (token) => {
  const decoded = token ? jwt.decode(token) : null;

  if (!decoded || typeof decoded === 'string' || typeof decoded.exp !== 'number') return 0;

  return decoded.exp - Date.now() / 1000;
};

/** @returns {Promise<void>} */
const refreshExpiredTokens = async () => {
  if (getSecondsLeft(apiClient.accessToken) > MIN_ACCESS_TOKEN_TTL) return;

  if (!LocalStorageHelper.getRefreshToken()) return;

  await apiClient.silentAuthorization();
};

/**
 * Stops waiting for `gamayun:ready`.
 *
 * @returns {void}
 */
export const cancelGamayunLaunch = () => {
  if (!pendingLaunch) return;

  window.removeEventListener('message', pendingLaunch.listener);
  clearTimeout(pendingLaunch.timer);
  pendingLaunch = null;
};

/**
 * Must be called synchronously from the click handler: the tab is opened before any `await`,
 * otherwise the browser treats it as a pop-up and blocks it. A repeated call reuses the
 * `gamayun` tab and replaces the previous listener.
 *
 * @param {GamayunLaunchOptions} options
 * @returns {boolean} `false` if the browser did not open the tab
 */
export const launchGamayun = ({
  launchUrl, gamayunOrigin, getEmployee, onError,
}) => {
  // No `noopener`: Gamayun needs `window.opener` to ask for the session.
  const gamayunWindow = window.open(launchUrl, GAMAYUN_WINDOW_NAME);

  if (!gamayunWindow) return false;

  cancelGamayunLaunch();

  // The pair is refreshed while the tab is loading.
  const refreshing = refreshExpiredTokens();
  // The error is reported in `handOverSession`; this keeps it from being unhandled before `ready`.
  refreshing.catch(() => {});
  /** @type {PendingLaunch} */
  const launch = { listener: null, timer: null };

  const handOverSession = async () => {
    try {
      await refreshing;

      // The token could expire while Gamayun was loading; a failed refresh is not repeated.
      if (apiClient.accessToken) {
        await refreshExpiredTokens();
      }

      if (pendingLaunch !== launch) return;

      const { accessToken } = apiClient;
      const refreshToken = LocalStorageHelper.getRefreshToken();
      const employee = getEmployee();

      cancelGamayunLaunch();

      if (!accessToken || !refreshToken || !employee) return;

      gamayunWindow.postMessage({
        type: 'gamayun:session',
        access_token: accessToken,
        refresh_token: refreshToken,
        employee: {
          id: employee.id,
          email: employee.email,
          name: employee.name,
        },
      }, gamayunOrigin);
    } catch {
      if (pendingLaunch === launch) cancelGamayunLaunch();

      if (onError) onError();
    }
  };

  launch.listener = (event) => {
    if (event.origin !== gamayunOrigin || event.source !== gamayunWindow) return;

    if (!event.data || event.data.type !== 'gamayun:ready') return;

    handOverSession();
  };
  launch.timer = setTimeout(cancelGamayunLaunch, GAMAYUN_READY_TIMEOUT_MS);

  pendingLaunch = launch;
  window.addEventListener('message', launch.listener);

  return true;
};
